import { useEffect, useState } from 'react';
import type { LineaHistorial, ListaHistorial } from '../../../api/ops/clientes';
import { Chip } from '../../../componentes/Chip';
import { Desplegable } from '../../../componentes/Desplegable';
import { EstadoError } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import type { NombreIcono } from '../../../componentes/iconos';
import { Paginacion } from '../../../componentes/Paginacion';
import { estadoDeError } from '../../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../../logica/paginacion';
import type { ContextoClientes } from '../lista/contexto';
import { diaYHora, importe } from '../lista/textos';

/**
 * Nombre e icono de cada módulo que escribe en el historial (el `module` de la línea). Uno que el panel no conoce sale
 * con su clave y un icono genérico: el historial solo trae lo de los módulos contratados.
 */
const MODULOS: Readonly<Record<string, [string, NombreIcono]>> = {
    reservas: ['Reservas', 'calendar-check'],
    'reservas-sala': ['Reservas', 'calendar-check'],
    citas: ['Citas', 'calendar'],
    entradas: ['Entradas', 'ticket'],
    'eventos-entradas': ['Entradas', 'ticket'],
    listas: ['Listas', 'list-checks'],
    'listas-rrpp': ['Listas', 'list-checks'],
    pases: ['Pases y acceso', 'door-open'],
    'pases-acceso': ['Pases y acceso', 'door-open'],
    pagos: ['Pagos', 'credit-card'],
    canales: ['Conversaciones', 'message-circle'],
    clientes: ['Clientes', 'users'],
};

const modulo = (clave: string): [string, NombreIcono] => MODULOS[clave] ?? [clave, 'circle'];

/** Historial (2.3): lo de todos los módulos contratados, de lo más reciente a lo más antiguo, con filtro por módulo. */
export function Historial({ c, id }: { c: ContextoClientes; id: string }) {
    const pag = usePaginacionCursor();
    const [filtro, setFiltro] = useState('');
    const [lista, setLista] = useState<ListaHistorial | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [vistos, setVistos] = useState<string[]>([]);
    const { ops, negocio } = c;

    useEffect(() => {
        let vivo = true;
        ops.listCustomerTimeline(negocio, id, { cursor: pag.cursor, module: filtro === '' ? undefined : filtro })
            .then((l) => {
                if (vivo) {
                    setLista(l);
                    setError(null);
                    setVistos((v) => Array.from(new Set([...v, ...l.data.map((x) => x.module)])));
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, pag.cursor, filtro, intento]);

    if (error !== null && lista === null) {
        return <EstadoError seccion="el historial" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={c.movil} />;
    }
    if (lista === null) {
        return null;
    }

    return (
        <div className="rv-cl-seccion">
            {vistos.length > 1 && (
                <div className="rv-cl-estrecho">
                    <Desplegable
                        etiqueta="Módulo"
                        valor={filtro}
                        opciones={[{ valor: '', texto: 'Todos los módulos' }, ...vistos.map((m) => ({ valor: m, texto: modulo(m)[0] }))]}
                        onChange={(v) => {
                            setFiltro(v);
                            pag.reiniciar();
                        }}
                    />
                </div>
            )}
            {lista.data.length === 0 ? (
                <p className="rv-cl-vacio">Todavía no hay nada en su historial.</p>
            ) : (
                <div className="rv-cl-lineas">
                    {lista.data.map((l) => (
                        <Linea key={l.id} l={l} />
                    ))}
                </div>
            )}
            {(pag.hayAnterior || haySiguiente(lista.next_cursor)) && (
                <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(lista.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={c.movil} />
            )}
            <p className="rv-cl-nota">Solo de los módulos que tienes contratados.</p>
        </div>
    );
}

function Linea({ l }: { l: LineaHistorial }) {
    const [nombre, icono] = modulo(l.module);
    const dinero = l.amount == null ? null : importe(l.amount);
    return (
        <div className="rv-cl-entrada">
            <span className="rv-cl-entrada__baldosa">
                <Icono nombre={icono} />
            </span>
            <span className="rv-cl-linea">
                <strong>{l.summary ?? l.event_type}</strong>
                <span>{`${diaYHora(l.occurred_at)} • ${nombre}`}</span>
            </span>
            <span className="rv-cl-entrada__lado">
                {dinero !== null && <strong>{`${dinero.cifra} ${dinero.moneda}`}</strong>}
                {l.status != null && l.status !== '' && <Chip estado="confirmed">{l.status}</Chip>}
            </span>
        </div>
    );
}
